import type { LanguagePickerIcon as IconKind } from '../i18n/types';
import styles from './LanguagePickerIcon.module.css';

type LanguagePickerIconProps = {
  icon: IconKind;
  className?: string;
  size?: 'default' | 'large';
};

/** US and Israel flags for the language picker. */
export function LanguagePickerIcon({ icon, className, size = 'default' }: LanguagePickerIconProps) {
  if (icon === 'flag-us') {
    return <UsFlag className={className} size={size} />;
  }
  return <IsraelFlag className={className} size={size} />;
}

function flagDimensions(size: 'default' | 'large') {
  return size === 'large' ? { width: 32, height: 22 } : { width: 21, height: 15 };
}

function UsFlag({ className, size }: { className?: string; size: 'default' | 'large' }) {
  const { width, height } = flagDimensions(size);
  return (
    <svg
      className={[styles.flag, className].filter(Boolean).join(' ')}
      viewBox="0 0 110 80"
      width={width}
      height={height}
      aria-hidden
      role="img"
    >
      {Array.from({ length: 13 }, (_, i) => (
        <rect
          key={i}
          y={(80 / 13) * i}
          width="110"
          height={80 / 13}
          fill={i % 2 === 0 ? '#B22234' : '#FFFFFF'}
        />
      ))}
      <rect width="44" height={(80 / 13) * 7} fill="#3C3B6E" />
      {US_CANTON_STARS.map(({ cx, cy, r }) => (
        <path key={`${cx}-${cy}`} d={starPath(cx, cy, r, r * 0.38)} fill="#FFFFFF" />
      ))}
    </svg>
  );
}

function IsraelFlag({ className, size }: { className?: string; size: 'default' | 'large' }) {
  const { width, height } = flagDimensions(size);
  return (
    <svg
      className={[styles.flag, className].filter(Boolean).join(' ')}
      viewBox="0 0 110 80"
      width={width}
      height={height}
      aria-hidden
      role="img"
    >
      <rect width="110" height="80" fill="#FFFFFF" />
      <rect y="6" width="110" height="8" fill="#0038B8" />
      <rect y="66" width="110" height="8" fill="#0038B8" />
      <polygon fill="#0038B8" points={ISRAEL_STAR_UP} />
      <polygon fill="#0038B8" points={ISRAEL_STAR_DOWN} />
    </svg>
  );
}

/** Hexagram vertices (every other point of a regular hexagon). */
const ISRAEL_STAR_UP = israelStarTriangle([0, 2, 4]);
const ISRAEL_STAR_DOWN = israelStarTriangle([1, 3, 5]);

function israelStarTriangle(indices: number[]): string {
  const cx = 55;
  const cy = 40;
  const r = 14;
  return indices
    .map((i) => {
      const angle = (-90 + i * 60) * (Math.PI / 180);
      const x = cx + r * Math.cos(angle);
      const y = cy + r * Math.sin(angle);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
}

/** Simplified 50-star canton layout (5 even rows + 4 odd rows). */
const US_CANTON_STARS: { cx: number; cy: number; r: number }[] = (() => {
  const stars: { cx: number; cy: number; r: number }[] = [];
  const rowHeight = (80 / 13) * 7 / 9;
  for (let row = 0; row < 9; row += 1) {
    const cols = row % 2 === 0 ? 6 : 5;
    const offset = row % 2 === 0 ? 3.7 : 7.4;
    for (let col = 0; col < cols; col += 1) {
      stars.push({
        cx: offset + col * 7.4,
        cy: rowHeight * 0.55 + row * rowHeight,
        r: 1.55,
      });
    }
  }
  return stars;
})();

function starPath(cx: number, cy: number, outerR: number, innerR: number): string {
  const points: string[] = [];
  for (let i = 0; i < 10; i += 1) {
    const angle = -Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 === 0 ? outerR : innerR;
    points.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
  }
  return `M${points.join('L')}Z`;
}
